'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { isNativePlatform } from '@/lib/utils/platform'
import { IOS_APP_STORE_URL, PLAY_STORE_URL } from '@/lib/config/app-install'

/**
 * App Update Modal — shown when the native app version is below
 * the server's minimum required version.
 *
 * This is harmless to ship before the app is on the store:
 * - On web: never renders (isNativePlatform() returns false)
 * - On native with no API response: never renders
 * - Only triggers when BOTH conditions are true:
 *   1. Running in a native WebView
 *   2. Server responds with a minimum version higher than the native app version
 *
 * The version check hits /api/app-version which returns { minimumVersion: "1.0.0" }
 * The native app version is read from the Capacitor App plugin.
 */
export function AppUpdateModal() {
  const t = useTranslations('common')
  const [showModal, setShowModal] = useState(false)
  const [storeUrl, setStoreUrl] = useState<string>('')

  useEffect(() => {
    if (!isNativePlatform()) return

    let cancelled = false
    let removeAppStateListener: (() => void) | undefined

    async function checkVersion() {
      try {
        const { App } = await import('@capacitor/app')
        const { getPlatform } = await import('@/lib/utils/platform')

        const appInfo = await App.getInfo()
        const currentVersion = appInfo.version // e.g. "1.0.0"

        // Fetch minimum required version from server
        const res = await fetch('/api/app-version', { cache: 'no-store' })
        if (!res.ok) return

        const data = await res.json()
        const minimumVersion = data.minimumVersion as string

        if (!minimumVersion) return

        // Simple semver comparison
        if (isVersionBelow(currentVersion, minimumVersion)) {
          const platform = getPlatform()
          // Read store URLs from the central app-install config so any future
          // change (App Store ID, locale, package rename) flows here automatically.
          // The previous hardcoded values had drifted: the iOS URL was a
          // placeholder (`id0000000000`) that sent every Apple user prompted
          // to update straight to a non-existent listing.
          setStoreUrl(platform === 'ios' ? IOS_APP_STORE_URL : PLAY_STORE_URL)
          setShowModal(true)
        }
      } catch {
        // Version check is non-critical — never break the app
      }
    }

    void checkVersion()

    void import('@capacitor/app').then(({ App }) => {
      if (cancelled) return
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void checkVersion()
      }).then((handle) => {
        if (cancelled) {
          void handle.remove()
          return
        }
        removeAppStateListener = () => {
          void handle.remove()
        }
      })
    })

    return () => {
      cancelled = true
      removeAppStateListener?.()
    }
  }, [])

  if (!showModal) return null

  return (
    <div className="fixed inset-0 z-10000 flex items-center justify-center bg-black/60 p-6">
      <div className="mx-auto w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
          <svg
            className="h-7 w-7 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        </div>

        <h2 className="mb-2 text-xl font-semibold text-gray-900">{t('updateRequired')}</h2>
        <p className="mb-6 text-sm text-gray-600">{t('updateDescription')}</p>

        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          {t('updateNow')}
        </a>
      </div>
    </div>
  )
}

/**
 * Compare two semver strings. Returns true if `current` is below `minimum`.
 * Handles "1.0.0" vs "1.0.1", "1.0" vs "1.0.0", etc.
 */
function isVersionBelow(current: string, minimum: string): boolean {
  const currentParts = current.split('.').map(Number)
  const minimumParts = minimum.split('.').map(Number)

  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const curr = currentParts[i] || 0
    const min = minimumParts[i] || 0
    if (curr < min) return true
    if (curr > min) return false
  }

  return false // Equal versions
}
